import React, { useState } from 'react'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { useCopyToolbar } from 'components/hyper-copy/UseCopyToolbar'
import { getAdjustedDateRangeString } from 'features/charts/utils'
import {
  CardInner,
  CardLabel,
  CardValue,
  ChangePill,
  DownIcon,
  InfoCard,
  KebabSlot,
  LoadingIcon,
  TopRow,
  UpIcon,
} from 'features/summary/components/StatCard/styles'
import { StatCardMenu } from 'features/summary/components/StatCardMenu/StatCardMenu'
import { numberFormatter } from 'utils/number-format'

export type StatValue = {
  field: string
  value: number | null
}

type Props = {
  el: StatValue
  prev?: StatValue
  label: string
  title?: string
  projectId: string
  hasPrevPeriod: boolean
  prevPeriodText: string
  comparisonLoading: boolean
  dateFilter: { min: number; max: number } | undefined
  onCopyRow: () => void
}

export const StatCard: React.FC<Props> = ({
  el,
  prev,
  label,
  title,
  projectId,
  hasPrevPeriod,
  prevPeriodText,
  comparisonLoading,
  dateFilter,
  onCopyRow,
}) => {
  const { ref, isHyperCopy, selectForCopy } = useCopyToolbar({
    elementName: 'summary-info-card',
  })
  const [menuOpen, setMenuOpen] = useState(false)

  // Sentiment is an absolute delta (no division), so a prior value of 0 is
  // valid data. For counts/rating, a prior value of 0 causes division by
  // zero, which we treat the same as missing data.
  const hasPriorValue =
    hasPrevPeriod &&
    prev?.value != null &&
    (el.field === 'n_sentiment_avg' || prev.value !== 0)

  let percentChange: number | null = null
  if (hasPriorValue && el.value != null) {
    if (el.field === 'n_sentiment_avg') {
      percentChange = Math.round((el.value - prev!.value!) * 100) / 100
    } else {
      percentChange =
        Math.round(((el.value - prev!.value!) / prev!.value!) * 100) || 0
    }
  }

  const showChange = percentChange !== null && percentChange !== Infinity

  const changeLabel =
    percentChange === null
      ? null
      : el.field === 'n_sentiment_avg'
        ? `${(percentChange ?? 0) >= 0 ? '+' : ''}${percentChange}`
        : `${(percentChange ?? 0) >= 0 ? '+' : ''}${percentChange}%`

  const tooltipTitle =
    prev?.value != null
      ? `Compared to ${prev.value?.toLocaleString()} ${prevPeriodText}`
      : undefined

  return (
    <InfoCard
      ref={ref}
      className={isHyperCopy ? 'hyper-copy-active' : 'hyper-copy'}
      title={title}
      onClick={(e) => {
        if (isHyperCopy) {
          selectForCopy()
          e.stopPropagation()
        }
      }}
    >
      <CardInner>
        <TopRow>
          <CardLabel>{label}</CardLabel>
          {comparisonLoading && <LoadingIcon />}
          {showChange ? (
            <Tooltip content={tooltipTitle}>
              <ChangePill
                $positive={(percentChange ?? 0) > 0}
                $negative={(percentChange ?? 0) < 0}
              >
                {changeLabel}
                {(percentChange ?? 0) > 0 ? (
                  <UpIcon />
                ) : (percentChange ?? 0) < 0 ? (
                  <DownIcon />
                ) : (
                  ''
                )}{' '}
              </ChangePill>
            </Tooltip>
          ) : !hasPriorValue && !comparisonLoading ? (
            <Tooltip
              content={`No data in prior period, ${getAdjustedDateRangeString(dateFilter)}`}
            >
              <ChangePill $positive={false} $negative={false} $disabled>
                --
              </ChangePill>
            </Tooltip>
          ) : null}
        </TopRow>
        <CardValue>
          {el.field === 'n_rating_avg' || el.field === 'n_sentiment_avg'
            ? el.value != null
              ? numberFormatter(el.value)
              : 'N/A'
            : (el.value?.toLocaleString() ?? 'N/A')}
        </CardValue>
      </CardInner>
      <KebabSlot data-html2canvas-ignore={'true'}>
        <StatCardMenu
          open={menuOpen}
          onOpenChange={setMenuOpen}
          onCopyItem={selectForCopy}
          onCopyRow={onCopyRow}
          projectId={projectId}
          title={title}
        />
      </KebabSlot>
    </InfoCard>
  )
}
